import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Copy, Check, RefreshCw, Loader2, History, ArrowLeft, CircleAlert,
} from 'lucide-react';
import { api } from '../../lib/api';

/* Vue en lecture seule du prompt système réellement envoyé au modèle, avec sa
   version et l'historique des versions précédentes. Les messages de l'onglet
   Historique portent leur persona_version : on peut donc remonter au prompt
   exact qui a produit une réponse. */

const LABELS = {
  prenom: 'Prénom', moitie: 'Sa moitié', parcours: 'Parcours', metier: 'Métier',
  histoire: 'Leur histoire', passions: 'Passions', valeurs: 'Valeurs', fierte: 'Fiertés',
  ton: 'Ton', debit: 'Débit', registre: 'Registre', expressions: 'Expressions',
  jurons: 'Jurons', ouverture_cloture: 'Ouverture / clôture',
  reactions_emotions: 'Réactions', face_taquinerie: 'Face aux taquineries',
  face_gene: 'Face à la gêne', en_societe: 'En société',
  humour: 'Humour', traits: 'Traits', opinions: 'Opinions', autoderision: 'Autodérision',
  manies: 'Manies', signature: 'Signature', sujet_radeau: 'Sujet inépuisable',
  contradiction: 'Contradiction', mot_aux_amis: 'Mot aux amis', zones_rouges: 'Zones rouges',
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '—');

export default function AdminPrompt() {
  const [data, setData] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get('/api/admin/prompt'));
      setViewing(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openVersion = async (v) => {
    try {
      setViewing(await api.get(`/api/admin/prompt/${v}`));
    } catch (e) {
      setErr(e.message);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  if (loading && !data) return <div className="empty">{err || 'Chargement…'}</div>;
  if (!data) return <div className="empty">{err}</div>;

  const shown = viewing || data;
  const missing = Object.entries(data.fields)
    .flatMap(([, fields]) => fields.filter((f) => !f.filled));

  return (
    <div className="admin-grid">
      <div className="panel span2">
        <div className="console-head">
          <h3 className="panel-h"><FileText size={17} /> Prompt système</h3>
          <div className="console-actions">
            <button className="btn-ghost" onClick={() => copy(shown.compiled_prompt)}>
              {copied ? <Check size={15} /> : <Copy size={15} />} Copier
            </button>
            <button className="btn-ghost" onClick={load} disabled={loading}>
              {loading ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />} Actualiser
            </button>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-tile">
            <div className="stat-val">v{data.version}</div>
            <div className="stat-lab">version en vigueur</div>
          </div>
          <div className="stat-tile">
            <div className="stat-val">{data.completeness}%</div>
            <div className="stat-lab">du persona renseigné</div>
          </div>
          <div className="stat-tile">
            <div className="stat-val">{data.length.toLocaleString('fr-FR')}</div>
            <div className="stat-lab">caractères de prompt</div>
          </div>
          <div className="stat-tile">
            <div className="stat-val">{data.anecdotes.promptable}/{data.anecdotes.total}</div>
            <div className="stat-lab">souvenirs injectés</div>
          </div>
        </div>

        <div className="hint left">
          Dernière évolution : {fmt(data.updated_at)}. La version n'augmente que lorsque le prompt
          change réellement — un enregistrement sans effet ne la fait pas bouger.
        </div>

        {missing.length > 0 && (
          <div className="rz-note">
            <CircleAlert size={15} />
            <div>
              <b>{missing.length} champ{missing.length > 1 ? 's' : ''} encore vide{missing.length > 1 ? 's' : ''}</b> —
              chacun rend l'incarnation un peu plus juste :{' '}
              {missing.map((f) => LABELS[f.name] || f.name).join(', ')}.
            </div>
          </div>
        )}

        {viewing && (
          <div className="test ok">
            <ArrowLeft size={15} />
            Vous consultez la version {viewing.version} du {fmt(viewing.created_at)} —{' '}
            <button className="link-btn" onClick={() => setViewing(null)}>revenir à la version en vigueur</button>
          </div>
        )}

        {err && <div className="test err">{err}</div>}

        <div className="prompt-view">
          <div className="secret-label">
            Envoyé au modèle à chaque question — version {shown.version} · {shown.length.toLocaleString('fr-FR')} caractères
          </div>
          <pre className="prompt-full">{shown.compiled_prompt}</pre>
        </div>
      </div>

      <div className="panel span2">
        <h3 className="panel-h"><History size={17} /> Historique des versions</h3>
        <p className="panel-p">
          Chaque évolution du prompt est conservée. Les messages de l'onglet Historique indiquent la
          version en vigueur au moment de la réponse : c'est ici qu'on retrouve le prompt exact
          qui l'a produite.
        </p>
        <div className="version-list">
          {data.history.map((v) => (
            <button
              key={v.version}
              className={`version-row${(viewing?.version ?? data.version) === v.version ? ' active' : ''}`}
              onClick={() => (v.version === data.version ? setViewing(null) : openVersion(v.version))}
            >
              <span className="version-num">v{v.version}</span>
              <span className="version-date">{fmt(v.created_at)}</span>
              <span className="version-len">{v.length.toLocaleString('fr-FR')} car.</span>
              {v.version === data.version && <span className="version-current">en vigueur</span>}
            </button>
          ))}
          {data.history.length === 0 && <div className="empty">Aucune version enregistrée.</div>}
        </div>
      </div>
    </div>
  );
}
